import { DropzoneField } from '@lfg/ui/components';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadIcon from '@mui/icons-material/Upload';
import { Box, Button, CircularProgress, IconButton, LinearProgress, Stack, Typography, alpha } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { colors } from 'src/global/styles/colors';
import { useNFTAssetUpload } from 'src/hooks/useNFTAssetUpload';

interface NFTAssetUploadProps {
  programId: string;
  disabled?: boolean;
  error?: string;
  initialAsset?: {
    id: string;
    name: string;
    size: number;
  } | null;
  onUploadComplete?: (assetId: string) => void;
  onClear?: () => void;
}
const maxSize = 5 * 1024 * 1024 * 1024; // 5GB in bytes

export function NFTAssetUpload({
  programId,
  disabled = false,
  error,
  initialAsset,
  onUploadComplete,
  onClear,
}: NFTAssetUploadProps) {
  const { uploadFile, progress, isUploading, error: uploadError, reset: resetUpload } = useNFTAssetUpload();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(initialAsset?.name || null);
  const [uploadedAssetId, setUploadedAssetId] = useState<string | null>(initialAsset?.id || null);

  const handleFileSelect = useCallback((files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];

    // Validate file type
    if (!file.name.endsWith('.zip')) {
      toast.error('Please upload a ZIP file');
      return;
    }

    // Validate file size (5GB max)
    if (file.size > maxSize) {
      toast.error('File size must be less than 5GB');
      return;
    }

    // Store the selected file (don't upload yet)
    setSelectedFile(file);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !programId) return;

    // Upload the file
    const result = await uploadFile(selectedFile, programId);

    if (result) {
      // Store both file name and asset ID
      setUploadedFileName(result.name);
      setUploadedAssetId(result.assetId);
      setSelectedFile(null); // Clear selected file after successful upload
      toast.success('NFT assets uploaded successfully');
      // Pass assetId to form (not the file name)
      onUploadComplete?.(result.assetId);
    } else if (uploadError) {
      toast.error(uploadError);
    }
  }, [selectedFile, programId, uploadFile, uploadError, onUploadComplete]);

  const handleDeleteSelected = useCallback(() => {
    setSelectedFile(null);
  }, []);

  const handleClearUpload = useCallback(() => {
    setUploadedFileName(null);
    setUploadedAssetId(null);
    setSelectedFile(null);
    resetUpload();
    onClear?.();
  }, [resetUpload, onClear]);

  // Prevent leaving page during upload
  useEffect(() => {
    if (!isUploading) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Show browser's native confirmation dialog
      e.preventDefault();
      // Modern browsers require returnValue to be set
      e.returnValue = 'Upload in progress. Are you sure you want to leave? Your upload will be lost.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploading]);

  // Uploading State
  if (isUploading) {
    return (
      <Box
        sx={{
          border: `1px dashed ${colors.neutral015}`,
          backgroundColor: colors.neutral03,
          borderRadius: '4px',
          padding: '32px',
          height: '200px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}>
        <CircularProgress
          variant='determinate'
          value={progress.percentage}
          size={60}
          sx={{
            color: colors.brand300,
          }}
        />
        <Typography color={colors.white} fontSize={'16px'} fontWeight={500}>
          Uploading... {progress.percentage}%
        </Typography>
        <LinearProgress
          variant='determinate'
          value={progress.percentage}
          sx={{
            width: '100%',
            maxWidth: '400px',
            height: '8px',
            borderRadius: '4px',
            backgroundColor: colors.neutral015,
            '& .MuiLinearProgress-bar': {
              backgroundColor: colors.brand300,
            },
          }}
        />
        <Typography color={colors.neutral90} fontSize={'14px'}>
          {(progress.loaded / (1024 * 1024)).toFixed(2)} MB / {(progress.total / (1024 * 1024)).toFixed(2)} MB
        </Typography>
      </Box>
    );
  }

  // Uploaded State
  if (uploadedFileName) {
    return (
      <Box
        sx={{
          border: `1px solid ${colors.brand300}`,
          backgroundColor: alpha(colors.brand300, 0.04),
          borderRadius: '4px',
          padding: '24px',
          height: '200px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
        }}>
        <CloudUploadIcon sx={{ fontSize: 48, color: colors.brand300 }} />
        <Typography color={colors.white} fontSize={'16px'} fontWeight={500}>
          {uploadedFileName}
        </Typography>
        <Button
          variant='text'
          onClick={handleClearUpload}
          disabled={disabled}
          sx={{
            color: colors.red300,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: alpha(colors.red300, 0.1),
            },
          }}>
          Remove File
        </Button>
      </Box>
    );
  }

  // File Selected State (before upload)
  if (selectedFile) {
    return (
      <Box
        sx={{
          border: `1px solid ${colors.neutral60}`,
          backgroundColor: colors.neutral03,
          borderRadius: '4px',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}>
        {/* File Info */}
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack direction='row' alignItems='center' gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '8px',
                backgroundColor: colors.neutral015,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <CloudUploadIcon sx={{ fontSize: 24, color: colors.neutral90 }} />
            </Box>
            <Stack>
              <Typography color={colors.white} fontSize={'16px'} fontWeight={500}>
                {selectedFile.name}
              </Typography>
              <Typography color={colors.neutral90} fontSize={'14px'}>
                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </Typography>
            </Stack>
          </Stack>
          <IconButton
            onClick={handleDeleteSelected}
            disabled={disabled}
            sx={{
              color: colors.neutral90,
              '&:hover': {
                color: colors.red300,
                backgroundColor: alpha(colors.red300, 0.1),
              },
            }}>
            <DeleteIcon />
          </IconButton>
        </Stack>

        {/* Upload Button */}
        <Button
          variant='contained'
          startIcon={<UploadIcon />}
          onClick={handleUpload}
          disabled={disabled}
          sx={{
            backgroundColor: colors.brand300,
            color: colors.white,
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.brand400,
            },
          }}>
          Upload File
        </Button>
      </Box>
    );
  }

  // Default State - Dropzone
  return (
    <DropzoneField
      disabled={disabled}
      maxFiles={1}
      maxSize={maxSize}
      containerSx={{
        border: `1px dashed ${error ? colors.error : colors.neutral015}`,
        backgroundColor: colors.neutral03,
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        maxHeight: '200px',
      }}
      onDrop={handleFileSelect}
      files={[]}
      onClear={() => {}}
      accept={{
        'application/zip': ['.zip'],
        'application/x-zip-compressed': ['.zip'],
      }}
    />
  );
}
